import entryMixin from '../../assets/mixins/entry';
import orderCommonMixin from '../../assets/mixins/order/order-common';
import langs from '../../assets/locale/lang/order';
import { PAYMENT_METHODS } from '../../components/order/const';
export default {
  langs,

  mixins: [entryMixin, orderCommonMixin],

  props: {
    orderSdk: {
      type: Object,
    },

    /**
     * 订单号
     */
    orderNo: {
      type: String,
      default: ''
    },

    /**
     * 后端主动获取微信支付结果。
     * ps: 在微信支付成功支付后主动获, 其余场景不做主动获取, 否则会有性能问题。
     */
    forceSyncPayStatus: {
      type: Boolean,
      default: false
    },

    /**
     * 初始化支付方式
     */
    initPaymentMethod: {
      type: String,
      default: PAYMENT_METHODS.WECHAT
    },

    /**
     * 是否禁用支付方式
     */
    disabledPaymentMethod: {
      type: Boolean,
      default: false
    },

    /**
     * 是否显示支付方式
     */
    showPaymentMethod: {
      type: Boolean,
      default: true
    },

    /**
     * 是否在确认支付后轮询订单状态
     */
    isPollingApiAfterConfirmPay: {
      type: Boolean,
      default: false
    },

    /**
     * 轮询订单状态的间隔时间
     */
    pollingApiAfterConfirmPayMs: {
      type: Number,
      default: 2000
    },

    /**
     * 排除的支付方式
     */
    excludePaymentMethods: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      /** 订单数据 */
      orderData: undefined,

      /** 支付方式 */
      paymentMethod: this.initPaymentMethod,

      /** 轮询订单状态的定时器 */
      pollingApiAfterConfirmPayInterval: null,
    };
  },

  computed: {
    /** 是否显示渠道码 */
    hasCustomerService() {
      // 待支付、已取消不显示渠道码
      if (!this.isWaitPay && !this.isClose && this.orderData.products?.[0]?.customerServiceType) {
        return true;
      }

      return false;
    },

    /** 渠道码地址 */
    customerServiceContact() {
      return this.orderData.products?.[0]?.customerServiceContact;
    },

    /** 渠道码提示文案 */
    customerServiceTips() {
      return this.customerServiceContact ? this.iarTrans('orderDetail.qrCodeTips') : this.iarTrans('orderDetail.qrCodeExpiredTips');
    },

    /** 虚拟商品，无物流 */
    isVirtual() {
      const { DELIVERY_TYPE } = this.orderSdk;
      return this.orderData.products?.[0]?.deliveryType === DELIVERY_TYPE.Virtual;
    },

    isWaitPay() {
      const { ORDER_STATUS } = this.orderSdk;
      return this.orderData?.status === ORDER_STATUS.WAIT_PAY;
    },

    isClose() {
      const { ORDER_STATUS } = this.orderSdk;
      return this.orderData?.status === ORDER_STATUS.CLOSE;
    },

    isDelivering() {
      const { ORDER_STATUS } = this.orderSdk;
      return this.orderData?.status === ORDER_STATUS.DELIVERING;
    },

    isFinish() {
      const { ORDER_STATUS } = this.orderSdk;
      return this.orderData?.status === ORDER_STATUS.FINISH;
    }
  },

  watch: {
    orderNo(val) {
      if (val) this.getOrderDetail();
    }
  },

  mounted() {
    this.getOrderDetail(this.forceSyncPayStatus);

    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    window.addEventListener('visibilitychange', this.handleVisibilityChange);

    if (this.isPollingApiAfterConfirmPay) {
      this.pollingApiAfterConfirmPay();
    }
  },

  beforeDestroy() {
    window.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.clearPollingApiAfterConfirmPay();
  },

  methods: {
    _getCoreSdk() {
      return this.orderSdk;
    },

    /**
     * 获取订单详情
     * @param {*} isSyncPayStatus 是否到微信拉取订单状态, 主动更新。注意仅微信支付成功后调用一次
     * @returns void
     */
    async getOrderDetail(isSyncPayStatus = false) {
      if (!this.orderSdk) return;

      const { orderNo } = this;
      const data = await this.orderSdk.getOrderDetail({
        orderNo,
        syncPayStatus: isSyncPayStatus ? 'Y' : 'N'
      });

      this.orderData = data;
    },

    /**
     * 点击按钮 (待支付 | 待收货)
     */
    async handleOrderBtnClick() {
      const { status, orderNo } = this.orderData;
      const { ORDER_STATUS } = this.orderSdk;
      if (status === ORDER_STATUS.WAIT_PAY) {
        this.handleConfirmPay(orderNo, {
          paymentMethod: this.paymentMethod
        });
      } else {
        this.handleConfirmReceipt(orderNo);
      }
    },

    /**
     * 确认收货
     * @param {*} orderNo 订单号
     * @returns void
     */
    async handleConfirmReceipt(orderNo) {
      const isConfirm = await this.toConfirmReceipt(orderNo);
      if (!isConfirm) {
        return;
      }
      this.getOrderDetail();
    },

    /**
     * 确认支付
     * @param {*} orderNo 订单号
     * @returns void
     */
    async handleConfirmPay(orderNo, { paymentMethod }) {
      const hasPay = await this.toConfirmPay(orderNo, { paymentMethod });
      if (!hasPay) {
        return;
      }
      this.getOrderDetail(true);
    },

    /**
     * 页面切换回来时，调用接口更新订单详情
     */
    async handleVisibilityChange() {
      // 判断页面是否可见
      const visible = document.visibilityState === 'visible';
      if (!visible) return;

      // 获取当前订单状态
      const currentStatus = this.orderData?.status;

      // 非待支付状态，不更新订单详情
      if (currentStatus !== this.orderSdk.ORDER_STATUS.WAIT_PAY) {
        return;
      }

      // 第一次请求订单详情
      await this.getOrderDetail();
      let newStatus = this.orderData?.status;

      // 特殊逻辑处理：如果状态未变，等待2秒后重试一次
      if (newStatus === this.orderSdk.ORDER_STATUS.WAIT_PAY) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        await this.getOrderDetail();
        newStatus = this.orderData?.status;
      }

      const isPaySuccess = this.checkPaySuccess(newStatus);

      // 从未支付到完成支付的状态，触发pay-success事件
      if (isPaySuccess) {
        this.$emit('pay-success', this.orderData.orderNo, { paymentMethod: this.paymentMethod });
      }
    },

    /**
     * 检查是否支付成功, 需要之前的状态是WAIT_PAY
     * @param {*} status 订单状态
     * @returns boolean
     */
    checkPaySuccess(status) {
      const successStatus = [
        this.orderSdk.ORDER_STATUS.WAIT_DELIVERY,
        this.orderSdk.ORDER_STATUS.DELIVERING,
        this.orderSdk.ORDER_STATUS.FINISH
      ];

      return successStatus.includes(status);
    },

    /**
     * 轮询订单状态
     */
    pollingApiAfterConfirmPay() {
      this.clearPollingApiAfterConfirmPay();

      this.pollingApiAfterConfirmPayInterval = setInterval(async () => {
        const { ORDER_STATUS } = this.orderSdk;
        const { status } = this.orderData;
        if (status === ORDER_STATUS.WAIT_PAY) {
          await this.getOrderDetail();
          const isPaySuccess = this.checkPaySuccess(this.orderData?.status);
          if (isPaySuccess) {
            this.$emit('pay-success', this.orderData.orderNo, { paymentMethod: this.paymentMethod });
            this.clearPollingApiAfterConfirmPay();
          }
        }
      }, this.pollingApiAfterConfirmPayMs);
    },

    /**
     * 清除轮询订单状态的定时器
     */
    clearPollingApiAfterConfirmPay() {
      clearInterval(this.pollingApiAfterConfirmPayInterval);
    }
  }
};
